import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

type NavigationOptions = {
  onSave: () => Promise<any>;
  hasUnsavedChanges: boolean;
  saveButtonText?: string;
  discardButtonText?: string;
  continueEditingText?: string;
};

export const useFormNavigation = ({
  onSave,
  hasUnsavedChanges,
  saveButtonText = 'Spara',
  discardButtonText = 'Avbryt',
  continueEditingText = 'Fortsätt redigera'
}: NavigationOptions) => {
  const navigate = useNavigate();
  const [showDialog, setShowDialog] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  // Handle browser back button and page refresh
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasUnsavedChanges) {
        // Standard way to show a confirmation dialog when closing/refreshing the page
        e.preventDefault();
        e.returnValue = '';
        return '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [hasUnsavedChanges]);

  // Custom navigation function that checks for unsaved changes
  const navigateSafely = useCallback((to: string) => {
    if (hasUnsavedChanges) {
      setPendingNavigation(to);
      setShowDialog(true);
    } else {
      navigate(to);
    }
  }, [hasUnsavedChanges, navigate]);

  // Handle save and navigate
  const handleSave = useCallback(async () => {
    if (!pendingNavigation) return;
    
    try {
      setIsSaving(true);
      await onSave();
      navigate(pendingNavigation);
    } catch (error) {
      console.error('Failed to save:', error);
      // Error is handled in the onSave function
    } finally {
      setIsSaving(false);
      setShowDialog(false);
      setPendingNavigation(null);
    }
  }, [pendingNavigation, onSave, navigate]);

  // Handle discard changes
  const handleDiscard = useCallback(() => {
    if (pendingNavigation) {
      setShowDialog(false);
      navigate(pendingNavigation);
      setPendingNavigation(null);
    }
  }, [pendingNavigation, navigate]);

  // Handle continue editing
  const handleContinueEditing = useCallback(() => {
    setShowDialog(false);
    setPendingNavigation(null);
  }, []);

  return {
    navigateSafely,
    showDialog,
    handleSave,
    handleDiscard,
    handleContinueEditing,
    isSaving,
    saveButtonText,
    discardButtonText,
    continueEditingText
  };
}; 